/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1783382400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jul 07 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sinch-campaigns",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Sinch Campaigns",
          "depth": 0,
          "id": "connect-to-sinch-campaigns"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Audience",
          "depth": 1,
          "id": "audience"
        }, {
          "value": "Trigger campaigns",
          "depth": 1,
          "id": "trigger-campaigns"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Per-row errors on campaign triggers",
          "depth": 1,
          "id": "per-row-errors-on-campaign-triggers"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ol: "ol",
      li: "li",
      ul: "ul",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#audience",
                children: _jsx(_components.strong, {
                  children: "Audience"
                })
              })
            }), _jsx(_components.td, {
              children: "Replace the contents of an existing Sinch audience with the current rows in your Hightouch model."
            }), _jsx(_components.td, {
              children: "Mirror"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.sinch.com/docs/campaigns/audiences/",
                children: "Audiences API"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#trigger-campaigns",
                children: _jsx(_components.strong, {
                  children: "Campaign trigger"
                })
              })
            }), _jsx(_components.td, {
              children: "Trigger an existing Sinch campaign for the contacts in your model, with optional template variables and a shoot tag."
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.sinch.com/docs/campaigns/api/campaigns-api/#send-to-contacts",
                children: "Send to contacts"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-sinch-campaigns",
        children: "Connect to Sinch Campaigns"
      }), "\n", _jsx(_components.p, {
        children: "To connect Sinch Campaigns to Hightouch you need the API key from your Sinch Campaigns console. It's a pre-encoded Basic-auth token, visible under your account settings."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to the ", _jsx(_components.a, {
              href: "https://app.hightouch.com/destinations",
              children: "Destinations overview"
            }), " page and select ", _jsx(_components.strong, {
              children: "Add destination"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Sinch Campaigns"
            }), " from the destination catalog."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter the following credentials, then select ", _jsx(_components.strong, {
              children: "Continue"
            }), ":"]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Region"
              }), ": the Sinch Campaigns region your account is provisioned in. Choose ", _jsx(_components.strong, {
                children: "European Union"
              }), " or ", _jsx(_components.strong, {
                children: "United States"
              }), ". Sinch hosts EU and US tenants separately, so picking the wrong one fails authentication on save."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "API key"
              }), ": your Sinch Campaigns API key, copied from the console. It's already base64-encoded, so Hightouch uses it directly as the Basic auth header — there's no separate secret to enter. Hightouch uses it to mint short-lived bearer tokens for the Campaigns API and JWTs for the Audiences API."]
            }), "\n"]
          }), "\n", _jsxs(_components.p, {
            children: ["When you select ", _jsx(_components.strong, {
              children: "Continue"
            }), ", Hightouch authenticates against both the Sinch Campaigns API and the Sinch Audiences API. If only one check fails, the test-connection step calls out which API to investigate — usually an account that isn't provisioned for Audiences, or a key from the wrong region."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The single API key authorizes both APIs. The Sinch account Hightouch reads and\nwrites audiences against is derived from the key itself, so there's nothing\nelse to configure."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "audience",
        children: "Audience"
      }), "\n", _jsx(_components.p, {
        children: "Audience syncs keep a Sinch audience in lockstep with a Hightouch model. Each sync run uploads the model's current contents as a CSV and replaces the audience membership wholesale."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "When you configure the sync, you can pick an existing Sinch audience or create\na new one by name. Hightouch populates the existing-audience dropdown by\npaging through every audience in your Sinch account."
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Hightouch refuses to upload a zero-row file. If the source query returns no\nrows, the sync errors instead of clearing the Sinch audience. Wiping an\naudience by accident is almost always a query bug, not the intent."
        })
      }), "\n", _jsx(_components.h4, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Audience syncs run in ", _jsx(_components.strong, {
          children: "Mirror"
        }), " mode. Each run reuploads the full membership. Sinch's Audiences API doesn't expose an append or remove operation, so incremental updates aren't possible."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching",
        children: "Configure record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Each row must include a phone number in ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/E.164",
          children: "E.164 format"
        }), " (for example, ", _jsx(_components.code, {
          children: "+5215512345678"
        }), "). Sinch identifies audience contacts by their MSISDN."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Destination field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Phone (MSISDN, E.164)"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsxs(_components.td, {
              children: ["Mapped from a column of ", _jsx(_components.code, {
                children: "phone"
              }), " type. Becomes the ", _jsx(_components.code, {
                children: "msisdn"
              }), " column in the uploaded CSV."]
            })]
          })
        })]
      }), "\n", _jsx(_components.h4, {
        id: "map-fields",
        children: "Map fields"
      }), "\n", _jsx(_components.p, {
        children: "Add a mapping for each extra attribute you want to attach to contacts in the audience. Each mapping becomes a column in the uploaded CSV, and the mapping name is the column header Sinch sees."
      }), "\n", _jsxs(_components.p, {
        children: ["Mapping names must use lowercase letters, digits, and underscores, and must start with a letter (for example, ", _jsx(_components.code, {
          children: "first_name"
        }), "). Date values are converted to ISO 8601 before being written to the CSV."]
      }), "\n", _jsx(_components.h3, {
        id: "trigger-campaigns",
        children: "Trigger campaigns"
      }), "\n", _jsx(_components.p, {
        children: "Campaign trigger syncs fire an existing Sinch campaign for the rows in your model. Each row becomes one recipient, and positional template variables in the campaign template are filled from the row's mappings."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Only campaigns marked ", _jsx(_components.strong, {
            children: "ready_to_shoot"
          }), " in Sinch appear in the campaign\ndropdown. Draft, paused, or unpublished campaigns must be published in the\nSinch console first."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "sync-mode-1",
        children: "Sync mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Campaign trigger syncs run in ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode. Each batch of rows is submitted to Sinch's ", _jsx(_components.code, {
          children: "send-to-contacts"
        }), " endpoint, which returns a task identifier. Hightouch then polls ", _jsx(_components.code, {
          children: "/tasks/{task_id}"
        }), " every 5 seconds until Sinch reports the task as finished."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch sends recipients in batches of 500 with a parallelization of 1. Sinch's API rejects concurrent sends to the same campaign, so a sync to a single campaign always runs sequentially. Syncs that span multiple campaigns are still grouped per-campaign before submission."
      }), "\n", _jsx(_components.h4, {
        id: "map-fields-1",
        children: "Map fields"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Destination field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Campaign"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsxs(_components.td, {
              children: ["The Sinch campaign to send. Pick from the dropdown (column or constant). Only campaigns marked ", _jsx(_components.code, {
                children: "ready_to_shoot"
              }), " are available."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Phone"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "Source column carrying the recipient's phone number in E.164 format. Sinch silently drops rows that don't parse."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Shoot tag"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsxs(_components.td, {
              children: ["Optional column or constant. Tags every send in this run so the results roll up together under the Sinch ", _jsx(_components.strong, {
                children: "Shoot Stats"
              }), " endpoint. If omitted, Sinch generates one."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "map-template-variables",
        children: "Map template variables"
      }), "\n", _jsxs(_components.p, {
        children: ["Sinch substitutes mapped values into the campaign template ", _jsx(_components.strong, {
          children: "positionally"
        }), ". The first mapping becomes ", _jsx(_components.code, {
          children: "[[A]]"
        }), ", the second becomes ", _jsx(_components.code, {
          children: "[[B]]"
        }), ", and so on. Add one mapping per template placeholder, in the same order they appear in the template. The mapping name is for your reference and is not sent to Sinch."]
      }), "\n", _jsx(_components.p, {
        children: "Date values are converted to ISO 8601 before being substituted, since Sinch's templates render the raw text as-is."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, verify the upload in Sinch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience syncs:"
          }), " open the audience in the Sinch console and confirm the contact count matches the row count from your model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Campaign trigger syncs:"
          }), " check the campaign's stats by shoot tag. If you configured a shoot tag, Sinch rolls every send from this run under it; otherwise, use the run's timestamp."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "per-row-errors-on-campaign-triggers",
        children: "Per-row errors on campaign triggers"
      }), "\n", _jsxs(_components.p, {
        children: ["When Sinch reports that a ", _jsx(_components.code, {
          children: "send-to-contacts"
        }), " task failed, Hightouch can't always map the failure to specific rows — Sinch's error payload isn't consistently keyed across error types. Hightouch surfaces Sinch's full error message against every recipient in the failing batch, so the ", _jsx(_components.a, {
          href: "#live-debugger",
          children: "Live debugger"
        }), " is the most reliable place to see what went wrong."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
